import React, { useState, useEffect, forwardRef } from 'react';
import { customPropertyService } from '../services';
import { Checkbox, formatSelectOptions, Input, Select, TextareaAutosize } from '../components';
import { useGlobalState, useMessage } from '../hooks';
import { getBatchEditValue } from '../helpers';

const _AddCustomPropertyListValueForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    custom_property_id: null,
    custom_property_list_value_name: '',
    custom_property_list_value_description: '',
    custom_property_list_value_code: '',
  };

  const initialBatchEditItems = {
    batch_custom_property_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentCustomProperty] = useGlobalState('currentCustomProperty');
  const [displayNames] = useGlobalState('displayNames');
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [formData, setFormData] = useState(initialFormState);

  const { hideMessage } = useMessage();

  const [customProperties, setCustomProperties] = useState([]);

  useEffect(() => {
    // Get form data
    fetchCustomProperties();
  }, []);

  const fetchCustomProperties = () => {
    customPropertyService.getAll().then(res => {
      if (res) {
        // Only work with Custom Properties that have type = "list"
        const customPropertyTypes = res.custom_properties.filter(
          x => x.custom_property_type_code === 'list'
        );
        setCustomProperties(customPropertyTypes);
      }
    });
    return;
  };

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      // copy + edit
      setFormData(prev => {
        return {
          ...prev,
          custom_property_list_value_id: data[0].custom_property_list_value_id,
          custom_property_id: data[0].custom_property_id,
          custom_property_list_value_name: data[0].custom_property_list_value_name,
          custom_property_list_value_description: data[0].custom_property_list_value_description,
          custom_property_list_value_code: data[0].custom_property_list_value_code,
        };
      });
    } else if (data.length > 1 && mode !== 'add') {
      // bulk edit
      setBatchEdit(true);
      setFormData({
        custom_property_id: getBatchEditValue(data, 'custom_property_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        custom_property_id: currentCustomProperty.custom_property_id,
      });
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser, currentCustomProperty]);

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData({
      ...initialFormState,
      custom_property_id: currentCustomProperty.custom_property_id,
    });
  };

  const onSubmit = event => {
    event.preventDefault();
    setLoading(true);
    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          custom_property_list_value_id: item.custom_property_list_value_id,
          custom_property_list_value_name: item.custom_property_list_value_name,
          custom_property_list_value_code: item.custom_property_list_value_code,
          custom_property_list_value_description: item.custom_property_list_value_description,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
      };
    }

    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        //Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <React.Fragment>
            <div className="form-block vertical">
              <Input
                type="text"
                name="custom_property_list_value_name"
                label="Name"
                value={formData.custom_property_list_value_name}
                onChange={handleInputChange}
                disabled={loading}
                validation={
                  formErrors.custom_property_list_value_name
                    ? { valid: false, message: formErrors.custom_property_list_value_name }
                    : undefined
                }
              ></Input>
            </div>
            <div className="form-block vertical">
              <TextareaAutosize
                label="Description"
                name="custom_property_list_value_description"
                value={formData.custom_property_list_value_description}
                onChange={handleInputChange}
                disabled={loading}
                isOptional={true}
              />
            </div>
            <div className="form-block vertical">
              <Input
                type="text"
                name="custom_property_list_value_code"
                label="Code"
                value={formData.custom_property_list_value_code || ''}
                onChange={handleInputChange}
                title={displayNames?.custom_property_list_value_code?.display_name_description}
                disabled={loading}
                validation={
                  formErrors.custom_property_list_value_code
                    ? { valid: false, message: formErrors.custom_property_list_value_code }
                    : undefined
                }
                isOptional={true}
              />
            </div>
          </React.Fragment>
        )}
        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_custom_property_id}
                name="batch_custom_property_id"
                label="Edit Custom Property"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              ></Checkbox>
            </label>
          )}
          <Select
            name="custom_property_id"
            label={!batchEdit && 'Custom Property'}
            value={
              formData.custom_property_id === 'multiple' ? undefined : formData.custom_property_id
            }
            options={formatSelectOptions({
              options: customProperties,
              optionValue: 'custom_property_id',
              optionLabel: 'custom_property_name',
              optionIcon: 'custom_property_type_name',
              tooltip: 'custom_property_description',
            })}
            onInputChange={handleInputChange}
            placeholder={
              formData.custom_property === 'multiple' ? '< Multiple >' : 'Select Custom Property...'
            }
            isClearable={true}
            isNullable={false}
            onChange={handleInputChange}
            disabled={loading || (batchEdit && !batchEditItems.batch_custom_property_id)}
          ></Select>
        </div>
      </form>
    </div>
  );
};
export const AddCustomPropertyListValueForm = forwardRef(_AddCustomPropertyListValueForm);
